import {
  Box,
  Button,
  Modal
} from '@platform/ui-library';
import {
  FC,
  SyntheticEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import { KnownClientId, versionSearch } from '@app/const';
import {
  Input,
  Message,
  MessageStatus,
  Select
} from '@app/form_components';
import { useCustomNavigate, useServices } from '@app/hooks';
import { Design, Option } from '@app/types';
import { createUuid } from '@app/utilities';
import { Seed } from '@app/utilities/Seed';

const defaultDesignName = 'Default';

type FormValues = {
  client?: Option<string> | null;
  designName?: string | null;
  templateDesignId?: string | null;
  version: string;
};

export const HomeCreatePage: FC = () => {
  const {
    avsService,
    errorMessageService,
    lokiService
  } = useServices();
  const navigate = useCustomNavigate();
  const [archeTypeDesigns, setArcheTypeDesigns] = useState<Option<string>[] | null>(null);
  const [clients, setClients] = useState<Option<string>[] | null>(null);
  const [clientDesigns, setClientDesigns] = useState<Option<string>[] | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({ version: '1.0.0' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.querySelector('input')?.focus();
    (async () => {
      const [
        clients,
        archeTypeDesigns
      ] = await Promise.all([
        avsService.getClients(),
        avsService.getArchTypeDesigns()
      ]);
      setClients(Option.fromIdAndName(clients));
      setArcheTypeDesigns(Option.fromIdAndName(archeTypeDesigns));
      setFormValues({
        ...formValues,
        templateDesignId: archeTypeDesigns.find(({ name }) => {
          return name === defaultDesignName;
        })?.id
      });
    })();
  }, []);

  useEffect(() => {
    const { client } = formValues;
    const templateDesignId = (client?.value === KnownClientId.interactionsCommon) ? (
      null
    ) : (
      archeTypeDesigns?.find(({ label }) => {
        return label === defaultDesignName;
      })?.value
    );
    setFormValues({ ...formValues, templateDesignId });
    if (!client) {
      return;
    }
    let isLatest = true;
    (async () => {
      const designs = await avsService.getDesigns(client.value);
      if (isLatest) {
        setClientDesigns(Option.fromIdAndName(designs));
      }
    })();
    return () => {
      isLatest = false;
    };
  }, [formValues.client]);

  useEffect(() => {
    const { designName } = formValues;
    const existingDesign = clientDesigns?.find(({ label }) => {
      return label === designName;
    });
    setErrorMessage((existingDesign) ? `Design with name "${designName}" already exists for selected client.` : null);
  }, [clientDesigns, formValues.designName]);

  const onCreate = async (event: SyntheticEvent) => {
    event.preventDefault();
    const {
      client,
      designName,
      templateDesignId,
      version
    } = formValues;
    if (isLoading) {
      return;
    } else if (errorMessage) {
      errorMessageService.addError(errorMessage);
      return;
    } else if (!clientDesigns) {
      errorMessageService.addError('Please wait for existing client designs to load.');
      return;
    } else if (!versionSearch.test(version)) {
      errorMessageService.addError('Please use a valid version number (e.g. "1.0.0").');
      return;
    } else if (
      !client
      || !designName?.trim()
      || (
        // Template is optional for common modules.
        client.value !== KnownClientId.interactionsCommon
        && !templateDesignId
      )
    ) {
      errorMessageService.addError('Please fill in all the fields prior to submission.');
      return;
    }

    setIsLoading(true);
    try {
      let template: Design | null = null;
      if (templateDesignId) {
        const templateBranch = await avsService.getMainBranch(templateDesignId);
        template = (!templateBranch?.latestVersion) ? null : (
          await avsService.getDesign(templateBranch.id, templateBranch.latestVersion)
        );
      } else {
        template = Seed.getBlankApp(
          client.label,
          designName,
          version
        );
      }
      if (!template) {
        throw new Error('Unable to download the latest version of the main branch for the selected template design.');
      }

      const branchId = createUuid();
      const clientName = client.label;
      const serialized = Object.assign(template, {
      // Archetype.
        archetypeVersion: template.version,
        // Branch.
        _id: branchId,
        branchId: branchId,
        branchName: avsService.mainBranchName,
        // Client.
        client: clientName,
        clientId: client.value,
        clientName,
        repoClient: clientName,
        // Design (designId is set on save).
        application: designName,
        designName,
        repoApplication: designName,
        // Timestamps.
        lastUpdate: Date.now(),
        // Version.
        version,
        repoVersion: version
      });
      delete (serialized as Partial<Design>).designId;
      await lokiService.storeSerialized(serialized);
      navigate('..');
    } catch (error) {
      errorMessageService.addError(error as Error);
    }
    setIsLoading(false);
  };

  return (
    <Modal className="ui-box-medium" isOpen>
      <form onSubmit={onCreate}>
        <Box theme="noPadding">
          <div className="box header">
            <h2>Create Version</h2>
          </div>
          <div className="box content overflow-visible" ref={contentRef}>
            <Select
              fieldConfig={{
                className: 'flex-grow min-half',
                size: 'small'
              }}
              label="Client"
              isLoading={!clients}
              name="client"
              options={clients?.map((client) => {
                return {
                  label: client.label,
                  // Store full option as value.
                  value: client
                };
              })}
              onChange={(client) => {
                setFormValues({ ...formValues, client });
              }}
              value={formValues.client}
            />
            <div className="flex-gap flex-wrap">
              <Input
                fieldConfig={{ className: 'flex-grow', size: 'small' }}
                label="Design Name"
                name="designName"
                onChange={(designName) => {
                  setFormValues({
                    ...formValues,
                    designName
                  });
                }}
                placeholder="CustomerService"
                value={formValues.designName ?? ''}
              />
              <Input
                fieldConfig={{ className: 'flex-grow', size: 'small' }}
                label="Version"
                name="version"
                onChange={(version) => {
                  setFormValues({
                    ...formValues,
                    version
                  });
                }}
                value={formValues.version ?? ''}
              />
            </div>
            <Select
              fieldConfig={{
                className: 'flex-grow min-half',
                size: 'small'
              }}
              label="Template"
              isLoading={
                !archeTypeDesigns
                || (!!formValues.client && !clientDesigns)
              }
              name="template"
              options={[
                ...archeTypeDesigns ?? [],
                ...clientDesigns ?? []
              ]}
              onChange={(templateDesignId) => {
                setFormValues({ ...formValues, templateDesignId });
              }}
              value={formValues.templateDesignId}
            />
            <Message message={errorMessage} status={MessageStatus.error} />
          </div>
          <div className="box footer no-border">
            <Button
              onClick={() => {
                navigate('..');
              }}
              theme="outline"
              type="button"
            >
              {window.l.popup.cancel}
            </Button>
            <Button theme="default">
              {(isLoading) ? 'Loading...' : 'Create'}
            </Button>
          </div>
        </Box>
      </form>
    </Modal>
  );
};
